// src/components/workspace/costing/ProposalPricingView.tsx
// Shows auto-generated pricing for a proposal with editing capabilities

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Sparkles, DollarSign, Users, Package,
  Calculator, FileText, ChevronDown, ChevronUp, Save,
  Check, AlertTriangle, RefreshCw, Target,
  HelpCircle, Info, TrendingUp
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../lib/pricing/utils';
import {
  generatePricingFromProposal,
  GeneratedPricing,
} from '../../../lib/pricing/autoGenerator';
import toast from 'react-hot-toast';

// Tooltip helper component
function Tip({ text }: { text: string }) {
  return (
    <span className="group relative inline-flex ml-1 cursor-help">
      <HelpCircle className="h-3.5 w-3.5 text-bw-muted group-hover:text-bw-primary transition-colors" />
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 z-50">
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </span>
    </span>
  );
}

export function ProposalPricingView() {
  const navigate = useNavigate();
  const { id: proposalId } = useParams();
  const { profile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [proposal, setProposal] = useState<any>(null);
  const [contract, setContract] = useState<any>(null);
  const [pricing, setPricing] = useState<GeneratedPricing | null>(null);
  const [expandedCLINs, setExpandedCLINs] = useState<Set<string>>(new Set());
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile?.id && proposalId) {
      loadAndGenerate();
    }
  }, [profile, proposalId]);

  const loadAndGenerate = async () => {
    try {
      setLoading(true);
      setSaved(false);

      // Load proposal - matching ProposalWorkspace pattern exactly
      const { data: template, error: templateError } = await supabase
        .from('proposal_templates')
        .select('*')
        .eq('id', proposalId)
        .eq('profile_id', profile!.id)
        .single();

      if (templateError) {
        console.error('Error loading proposal:', templateError);
        toast.error('Failed to load proposal');
        navigate('/workspace/costing');
        return;
      }

      setProposal(template);

      // Load contract details
      let contractData: any = null;
      if (template.notice_id) {
        let { data: samContract } = await supabase
          .from('sam_contracts')
          .select('*')
          .eq('notice_id', template.notice_id)
          .maybeSingle();

        if (!samContract) {
          const result = await supabase
            .from('sam_contracts')
            .select('*')
            .eq('id', template.notice_id)
            .maybeSingle();
          samContract = result.data;
        }
        contractData = samContract;
      }
      setContract(contractData);

      // Load company indirect rates if available (wrapped in try/catch - table may not exist yet)
      let companyRates = null;
      try {
        const { data } = await supabase
          .from('company_indirect_rates')
          .select('fringe_rate, overhead_rate, ga_rate, profit_rate')
          .eq('profile_id', profile!.id)
          .maybeSingle();
        companyRates = data;
      } catch {
        // Table may not exist yet - that's fine, we'll use defaults
      }

      // Auto-generate pricing
      setGenerating(true);
      const generatedPricing = generatePricingFromProposal(
        template.content_json,
        contractData,
        companyRates
      );
      setPricing(generatedPricing);

      // Expand first CLIN by default
      if (generatedPricing.clins.length > 0) {
        setExpandedCLINs(new Set([generatedPricing.clins[0].id]));
      }

    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to generate pricing');
    } finally {
      setLoading(false);
      setGenerating(false);
    }
  };

  const toggleCLIN = (clinId: string) => {
    setExpandedCLINs(prev => {
      const next = new Set(prev);
      if (next.has(clinId)) next.delete(clinId);
      else next.add(clinId);
      return next;
    });
  };

  // Deep-clone pricing state to ensure React detects changes
  const clonePricing = (p: GeneratedPricing): GeneratedPricing => {
    return JSON.parse(JSON.stringify(p));
  };

  const updateCLINLaborHours = (clinId: string, categoryId: string, hours: number) => {
    if (!pricing) return;
    const updated = clonePricing(pricing);
    const clin = updated.clins.find(c => c.id === clinId);
    if (!clin) return;

    const lm = clin.laborMix.find(l => l.categoryId === categoryId);
    if (lm) {
      lm.hours = hours;
      clin.totalLabor = Math.round(clin.laborMix.reduce((sum, l) => sum + (l.hours * l.loadedRate), 0) * 100) / 100;
      clin.total = Math.round((clin.totalLabor + clin.totalODC) * 100) / 100;
    }

    updated.summary.totalLabor = Math.round(updated.clins.reduce((sum, c) => sum + c.totalLabor, 0) * 100) / 100;
    updated.summary.totalPrice = Math.round((updated.summary.totalLabor + updated.summary.totalODC) * 100) / 100;
    setPricing(updated);
    setSaved(false);
  };

  const updateCLINLaborBaseRate = (clinId: string, categoryId: string, newBaseRate: number) => {
    if (!pricing) return;
    const updated = clonePricing(pricing);
    const newLoadedRate = Math.round(newBaseRate * pricing.wrapRate * 100) / 100;

    // Update in the CLIN's labor mix
    const clin = updated.clins.find(c => c.id === clinId);
    if (clin) {
      const lm = clin.laborMix.find(l => l.categoryId === categoryId);
      if (lm) {
        lm.baseRate = newBaseRate;
        lm.loadedRate = newLoadedRate;
        clin.totalLabor = Math.round(clin.laborMix.reduce((sum, l) => sum + (l.hours * l.loadedRate), 0) * 100) / 100;
        clin.total = Math.round((clin.totalLabor + clin.totalODC) * 100) / 100;
      }
    }

    // Also update in the top-level labor categories list
    const cat = updated.laborCategories.find(c => c.id === categoryId);
    if (cat) {
      cat.baseRate = newBaseRate;
      cat.loadedRate = newLoadedRate;
    }

    // Also update the same category in OTHER CLINs so rates stay consistent
    for (const c of updated.clins) {
      if (c.id === clinId) continue;
      const otherLm = c.laborMix.find(l => l.categoryId === categoryId);
      if (otherLm) {
        otherLm.baseRate = newBaseRate;
        otherLm.loadedRate = newLoadedRate;
        c.totalLabor = Math.round(c.laborMix.reduce((sum, l) => sum + (l.hours * l.loadedRate), 0) * 100) / 100;
        c.total = Math.round((c.totalLabor + c.totalODC) * 100) / 100;
      }
    }

    updated.summary.totalLabor = Math.round(updated.clins.reduce((sum, c) => sum + c.totalLabor, 0) * 100) / 100;
    updated.summary.totalPrice = Math.round((updated.summary.totalLabor + updated.summary.totalODC) * 100) / 100;
    setPricing(updated);
    setSaved(false);
  };

  const updateLaborCategoryBaseRate = (categoryId: string, newBaseRate: number) => {
    if (!pricing) return;
    const updated = clonePricing(pricing);
    const newLoadedRate = Math.round(newBaseRate * pricing.wrapRate * 100) / 100;

    // Update top-level labor category
    const cat = updated.laborCategories.find(c => c.id === categoryId);
    if (cat) {
      cat.baseRate = newBaseRate;
      cat.loadedRate = newLoadedRate;
    }

    // Update in all CLINs that use this category
    for (const clin of updated.clins) {
      const lm = clin.laborMix.find(l => l.categoryId === categoryId);
      if (lm) {
        lm.baseRate = newBaseRate;
        lm.loadedRate = newLoadedRate;
        clin.totalLabor = Math.round(clin.laborMix.reduce((sum, l) => sum + (l.hours * l.loadedRate), 0) * 100) / 100;
        clin.total = Math.round((clin.totalLabor + clin.totalODC) * 100) / 100;
      }
    }

    updated.summary.totalLabor = Math.round(updated.clins.reduce((sum, c) => sum + c.totalLabor, 0) * 100) / 100;
    updated.summary.totalPrice = Math.round((updated.summary.totalLabor + updated.summary.totalODC) * 100) / 100;
    setPricing(updated);
    setSaved(false);
  };

  const updateODCAmount = (clinId: string, odcIndex: number, amount: number) => {
    if (!pricing) return;
    const updated = clonePricing(pricing);
    const clin = updated.clins.find(c => c.id === clinId);
    if (!clin || !clin.odcs[odcIndex]) return;

    clin.odcs[odcIndex].amount = amount;
    clin.totalODC = Math.round(clin.odcs.reduce((sum, o) => sum + o.amount, 0) * 100) / 100;
    clin.total = Math.round((clin.totalLabor + clin.totalODC) * 100) / 100;

    updated.summary.totalODC = Math.round(updated.clins.reduce((sum, c) => sum + c.totalODC, 0) * 100) / 100;
    updated.summary.totalPrice = Math.round((updated.summary.totalLabor + updated.summary.totalODC) * 100) / 100;
    setPricing(updated);
    setSaved(false);
  };

  const savePricingToProposal = async () => {
    if (!pricing || !proposal) {
      console.error('Cannot save: pricing or proposal is null', { pricing: !!pricing, proposal: !!proposal });
      toast.error('Nothing to save');
      return;
    }

    try {
      setSaving(true);
      console.log('Saving pricing to proposal:', proposal.id);

      // Build updated content_json - merge with existing data
      const updatedContentJson = {
        ...(proposal.content_json || {}),
        pricingStrategy: {
          laborCategories: pricing.laborCategories.map(cat => ({
            id: cat.id,
            title: cat.title,
            baseRate: cat.baseRate,
            loadedRate: cat.loadedRate,
            hours: cat.hours,
            headcount: cat.headcount,
            scaCode: cat.scaCode,
          })),
          clinStructure: pricing.clins.map(clin => ({
            id: clin.id,
            clinNumber: clin.clinNumber,
            title: clin.title,
            description: clin.description,
            type: clin.type,
            period: clin.period,
            laborMix: clin.laborMix.map(lm => ({
              categoryId: lm.categoryId,
              categoryTitle: lm.categoryTitle,
              hours: lm.hours,
              baseRate: lm.baseRate,
              loadedRate: lm.loadedRate,
            })),
            odcs: clin.odcs.map(o => ({ name: o.name, amount: o.amount })),
            totalLabor: clin.totalLabor,
            totalODC: clin.totalODC,
            totalPrice: clin.total,
          })),
          indirectRates: { ...pricing.indirectRates },
          wrapRate: pricing.wrapRate,
          costBreakdown: {
            totalLabor: pricing.summary.totalLabor,
            totalODC: pricing.summary.totalODC,
            totalContractValue: pricing.summary.totalPrice,
            contractType: pricing.summary.contractType,
            periodOfPerformance: pricing.summary.periodOfPerformance,
          },
          generatedAt: new Date().toISOString(),
        },
      };

      // Save to proposal_templates
      const { error: updateError } = await supabase
        .from('proposal_templates')
        .update({ content_json: updatedContentJson })
        .eq('id', proposal.id)
        .eq('profile_id', profile!.id);

      if (updateError) {
        console.error('Supabase update error:', updateError);
        throw updateError;
      }

      console.log('Pricing saved successfully to proposal_templates');

      // Update local proposal state so next save uses fresh data
      setProposal({ ...proposal, content_json: updatedContentJson });

      // Try to save indirect rates to company profile (non-blocking)
      try {
        const { data: existingRates } = await supabase
          .from('company_indirect_rates')
          .select('id')
          .eq('profile_id', profile!.id)
          .maybeSingle();

        if (!existingRates) {
          await supabase.from('company_indirect_rates').insert({
            profile_id: profile!.id,
            fringe_rate: pricing.indirectRates.fringe,
            overhead_rate: pricing.indirectRates.overhead,
            ga_rate: pricing.indirectRates.gAndA,
            profit_rate: pricing.indirectRates.profit,
          });
          console.log('Indirect rates saved to company profile');
        }
      } catch (ratesError) {
        // Non-blocking - company_indirect_rates table may not exist yet
        console.warn('Could not save indirect rates to company profile:', ratesError);
      }

      setSaved(true);
      toast.success('Pricing saved to proposal');
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast.error('Failed to save pricing. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-bw-primary" />
        <div className="text-center">
          <p className="text-bw-ink font-medium">
            {generating ? 'Generating pricing from your proposal...' : 'Loading proposal data...'}
          </p>
          <p className="text-sm text-bw-muted mt-1">
            Analyzing contract requirements, labor needs, and deliverables
          </p>
        </div>
      </div>
    );
  }

  if (!pricing) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-fit-moderate mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-bw-ink mb-2">Could not generate pricing</h2>
        <p className="text-bw-muted mb-4">The proposal may not have enough data to generate pricing automatically.</p>
        <button
          onClick={() => navigate('/workspace/costing')}
          className="px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-blue-700"
        >
          Back to Costing Studio
        </button>
      </div>
    );
  }

  const contractTitle = contract?.title || proposal?.template_name || 'Untitled Contract';
  const agency = contract?.agency || 'Unknown Agency';

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate(`/workspace/${proposalId}`)}
            className="text-sm text-bw-muted hover:text-bw-ink flex items-center mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Proposal
          </button>
          <h1 className="text-2xl font-bold text-bw-ink flex items-center">
            <Sparkles className="h-6 w-6 text-bw-primary mr-2" />
            AI-Generated Pricing
          </h1>
          <p className="text-bw-muted mt-1">{contractTitle} &mdash; {agency}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadAndGenerate}
            className="flex items-center px-3 py-2 text-sm border border-bw-border rounded-lg hover:bg-bw-surface-subtle"
            title="Re-analyze your proposal and generate fresh pricing"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Regenerate
          </button>
          <button
            onClick={() => navigate(`/workspace/${proposalId}/price-to-win`)}
            className="flex items-center px-3 py-2 text-sm border border-indigo-300 text-bw-primary rounded-lg hover:bg-bw-primary-tint"
            title="Compare your pricing against competitors and market data"
          >
            <Target className="h-4 w-4 mr-1" />
            Price-to-Win
          </button>
          <button
            onClick={savePricingToProposal}
            disabled={saving}
            className={`flex items-center px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
              saved
                ? 'bg-success-bg text-success hover:bg-green-200'
                : 'bg-bw-primary text-white hover:bg-blue-700'
            }`}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : saved ? (
              <Check className="h-4 w-4 mr-1" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            {saving ? 'Saving...' : saved ? 'Saved' : 'Save to Proposal'}
          </button>
        </div>
      </div>

      {/* How This Works Banner */}
      <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-bw-primary mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900">How AI Pricing Works</h3>
            <p className="text-sm text-bw-primary mt-1">
              We analyzed your proposal to auto-generate a complete pricing breakdown. Labor categories were extracted
              from your technical approach and deliverables, then multiplied by your wrap rate (indirect costs) to
              calculate fully-loaded rates. CLINs were built from your deliverables structure.
              <strong> Edit any number below, then click "Save to Proposal" to keep your changes.</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Summary Banner */}
      <div className="bg-bw-sidebar rounded-xl p-6 text-white">
        <div className="grid grid-cols-5 gap-4">
          <div>
            <div className="text-sm text-blue-200 flex items-center">
              Total Price
              <Tip text="The total proposed contract value including all labor costs and other direct costs (ODCs). This is the number the government sees." />
            </div>
            <div className="text-2xl font-bold">{formatCurrency(pricing.summary.totalPrice)}</div>
          </div>
          <div>
            <div className="text-sm text-blue-200 flex items-center">
              Total Labor
              <Tip text="Sum of all labor hours multiplied by fully-loaded rates across all CLINs. This is typically 70-85% of a services contract." />
            </div>
            <div className="text-xl font-semibold">{formatCurrency(pricing.summary.totalLabor)}</div>
          </div>
          <div>
            <div className="text-sm text-blue-200 flex items-center">
              Total ODC
              <Tip text="Other Direct Costs - non-labor expenses like travel, equipment, materials, and supplies that are billed directly to the contract." />
            </div>
            <div className="text-xl font-semibold">{formatCurrency(pricing.summary.totalODC)}</div>
          </div>
          <div>
            <div className="text-sm text-blue-200 flex items-center">
              Wrap Rate
              <Tip text="The multiplier applied to base labor rates. Includes fringe benefits, overhead, G&A, and profit. A 2.15x wrap rate means a $50/hr employee costs $107.50/hr fully loaded." />
            </div>
            <div className="text-xl font-semibold">{pricing.wrapRate.toFixed(2)}x</div>
          </div>
          <div>
            <div className="text-sm text-blue-200 flex items-center">
              Contract Type
              <Tip text="FFP = Firm Fixed Price (you absorb risk), T&M = Time & Materials (billed hourly), CPFF = Cost Plus Fixed Fee, IDIQ = Indefinite Delivery/Quantity." />
            </div>
            <div className="text-xl font-semibold">{pricing.summary.contractType}</div>
          </div>
        </div>

        {/* Indirect rates bar */}
        <div className="mt-4 flex items-center gap-2 text-sm flex-wrap">
          <span className="text-blue-200">Rates:</span>
          <span className="px-2 py-0.5 bg-white/15 rounded" title="Fringe: employee benefits like health insurance, PTO, FICA taxes">
            Fringe {pricing.indirectRates.fringe}%
          </span>
          <span className="px-2 py-0.5 bg-white/15 rounded" title="Overhead: indirect costs like rent, utilities, management, IT support">
            OH {pricing.indirectRates.overhead}%
          </span>
          <span className="px-2 py-0.5 bg-white/15 rounded" title="General & Administrative: company-wide costs like accounting, legal, HR, BD">
            G&A {pricing.indirectRates.gAndA}%
          </span>
          <span className="px-2 py-0.5 bg-white/15 rounded" title="Profit/Fee: your company's margin on the contract">
            Profit {pricing.indirectRates.profit}%
          </span>
          <span className="ml-auto text-blue-200">{pricing.summary.periodOfPerformance}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left: CLINs (2/3 width) */}
        <div className="col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-bw-ink flex items-center">
              <FileText className="h-5 w-5 text-bw-primary mr-2" />
              Contract Line Items (CLINs)
              <Tip text="CLINs are individual line items on your contract. Each CLIN represents a distinct deliverable or service area with its own pricing. The government evaluates and funds each CLIN separately." />
              <span className="ml-2 text-sm font-normal text-bw-muted">{pricing.clins.length} CLINs</span>
            </h2>
          </div>

          {/* CLIN Explainer */}
          <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-3 text-sm text-fit-moderate">
            <strong>Tip:</strong> Click on a CLIN to expand it. You can edit both the <strong>base rate</strong> and <strong>hours</strong> for
            each labor category. Changing a base rate will update the loaded rate and totals across all CLINs automatically.
          </div>

          {pricing.clins.map((clin) => (
            <div key={clin.id} className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
              {/* CLIN Header */}
              <button
                onClick={() => toggleCLIN(clin.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-bw-surface-subtle transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 bg-bw-primary/10 text-bw-primary text-sm font-mono font-semibold rounded">
                    {clin.clinNumber}
                  </span>
                  <div className="text-left">
                    <h3 className="font-medium text-bw-ink">{clin.title}</h3>
                    <p className="text-xs text-bw-muted">
                      {clin.type} &bull; {clin.period} period &bull; {clin.laborMix.length} labor categories
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="font-semibold text-bw-ink">{formatCurrency(clin.total)}</div>
                    <div className="text-xs text-bw-muted">
                      Labor: {formatCurrency(clin.totalLabor)} | ODC: {formatCurrency(clin.totalODC)}
                    </div>
                  </div>
                  {expandedCLINs.has(clin.id) ? (
                    <ChevronUp className="h-5 w-5 text-bw-muted" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-bw-muted" />
                  )}
                </div>
              </button>

              {/* CLIN Details */}
              {expandedCLINs.has(clin.id) && (
                <div className="border-t border-bw-border p-4 bg-bw-surface-subtle/50">
                  {clin.description && (
                    <p className="text-sm text-bw-muted mb-4 italic">{clin.description}</p>
                  )}

                  {/* Labor Mix Table */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-bw-ink mb-2 flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      Labor Mix
                      <Tip text="Each row is a labor category assigned to this CLIN. The 'Base Rate' is the employee's hourly pay. The 'Loaded Rate' includes all indirect costs (wrap rate). Edit the 'Hours' column to adjust staffing levels." />
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-bw-muted border-b border-bw-border">
                            <th className="pb-2 font-medium">Category</th>
                            <th className="pb-2 font-medium text-right">
                              Base Rate
                              <Tip text="The direct hourly rate paid to the employee before any indirect costs are added. Click to edit — the loaded rate and totals will update automatically." />
                            </th>
                            <th className="pb-2 font-medium text-right">
                              Loaded Rate
                              <Tip text="Base rate multiplied by the wrap rate ({pricing.wrapRate.toFixed(2)}x). This is what the government pays per hour. Updates automatically when you change the base rate." />
                            </th>
                            <th className="pb-2 font-medium text-right">
                              Hours
                              <Tip text="Total hours for this role on this CLIN. For a full-time employee working 1 year, this is typically 1,768-2,080 hours." />
                            </th>
                            <th className="pb-2 font-medium text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clin.laborMix.map((lm) => (
                            <tr key={lm.categoryId} className="border-b border-bw-border">
                              <td className="py-2 text-bw-ink">{lm.categoryTitle}</td>
                              <td className="py-2 text-right">
                                <div className="flex items-center justify-end gap-0.5">
                                  <span className="text-bw-muted text-xs">$</span>
                                  <input
                                    type="number"
                                    value={lm.baseRate}
                                    onChange={(e) => updateCLINLaborBaseRate(clin.id, lm.categoryId, parseFloat(e.target.value) || 0)}
                                    className="w-20 text-right px-1.5 py-1 border border-bw-border rounded text-sm focus:ring-2 focus:ring-bw-primary focus:border-bw-primary"
                                    min={0}
                                    step={5}
                                  />
                                  <span className="text-bw-muted text-xs">/hr</span>
                                </div>
                              </td>
                              <td className="py-2 text-right text-bw-muted">{formatCurrency(lm.loadedRate)}/hr</td>
                              <td className="py-2 text-right">
                                <input
                                  type="number"
                                  value={lm.hours}
                                  onChange={(e) => updateCLINLaborHours(clin.id, lm.categoryId, parseInt(e.target.value) || 0)}
                                  className="w-24 text-right px-2 py-1 border border-bw-border rounded text-sm focus:ring-2 focus:ring-bw-primary focus:border-bw-primary"
                                  min={0}
                                  step={40}
                                />
                              </td>
                              <td className="py-2 text-right font-medium text-bw-ink">
                                {formatCurrency(lm.hours * lm.loadedRate)}
                              </td>
                            </tr>
                          ))}
                          <tr className="font-medium bg-bw-surface-subtle">
                            <td className="pt-3 pb-2 text-bw-ink pl-1" colSpan={3}>Labor Subtotal</td>
                            <td className="pt-3 pb-2 text-right text-bw-ink">
                              {clin.laborMix.reduce((sum, lm) => sum + lm.hours, 0).toLocaleString()} hrs
                            </td>
                            <td className="pt-3 pb-2 text-right text-bw-ink">{formatCurrency(clin.totalLabor)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ODCs */}
                  {clin.odcs.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-bw-ink mb-2 flex items-center">
                        <Package className="h-4 w-4 mr-1" />
                        Other Direct Costs (ODCs)
                        <Tip text="Non-labor costs billed directly to the contract. Travel, equipment, materials, software licenses, etc. Edit amounts to match your estimate." />
                      </h4>
                      <table className="w-full text-sm">
                        <tbody>
                          {clin.odcs.map((odc, idx) => (
                            <tr key={idx} className="border-b border-bw-border">
                              <td className="py-2 text-bw-ink">{odc.name}</td>
                              <td className="py-2 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <span className="text-bw-muted">$</span>
                                  <input
                                    type="number"
                                    value={odc.amount}
                                    onChange={(e) => updateODCAmount(clin.id, idx, parseFloat(e.target.value) || 0)}
                                    className="w-28 text-right px-2 py-1 border border-bw-border rounded text-sm focus:ring-2 focus:ring-bw-primary focus:border-bw-primary"
                                    min={0}
                                    step={100}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                          <tr className="font-medium bg-bw-surface-subtle">
                            <td className="pt-3 pb-2 text-bw-ink pl-1">ODC Subtotal</td>
                            <td className="pt-3 pb-2 text-right text-bw-ink">{formatCurrency(clin.totalODC)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* CLIN Total */}
                  <div className="mt-4 pt-3 border-t-2 border-bw-border flex items-center justify-between">
                    <span className="font-semibold text-bw-ink">CLIN {clin.clinNumber} Total</span>
                    <span className="text-lg font-bold text-bw-primary">{formatCurrency(clin.total)}</span>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Save reminder */}
          {!saved && (
            <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-fit-moderate">
                <AlertTriangle className="h-4 w-4" />
                <span>You have unsaved changes. Click <strong>Save to Proposal</strong> to keep your pricing.</span>
              </div>
              <button
                onClick={savePricingToProposal}
                disabled={saving}
                className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 flex items-center gap-1"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save Now
              </button>
            </div>
          )}
        </div>

        {/* Right: Labor Categories & Summary */}
        <div className="space-y-4">
          {/* Labor Categories */}
          <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
            <h3 className="font-semibold text-bw-ink mb-1 flex items-center">
              <Users className="h-5 w-5 text-bw-primary mr-2" />
              Labor Categories
              <Tip text="These are the job roles identified from your proposal. Each has a base hourly rate and a loaded rate (after applying the wrap rate). The colored badges show where each role was found." />
            </h3>
            <p className="text-xs text-bw-muted mb-3">
              Roles extracted from your proposal data
            </p>
            <div className="space-y-2">
              {pricing.laborCategories.map((cat) => (
                <div key={cat.id} className="p-3 bg-bw-surface-subtle rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-bw-ink truncate">{cat.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-bw-muted flex items-center gap-0.5">
                          Base: $
                          <input
                            type="number"
                            value={cat.baseRate}
                            onChange={(e) => updateLaborCategoryBaseRate(cat.id, parseFloat(e.target.value) || 0)}
                            className="w-16 text-right px-1 py-0.5 border border-bw-border rounded text-xs focus:ring-1 focus:ring-bw-primary focus:border-bw-primary"
                            min={0}
                            step={5}
                          />
                          /hr
                        </span>
                        <span className="text-xs text-bw-muted">&rarr;</span>
                        <span className="text-xs font-medium text-bw-primary">
                          Loaded: {formatCurrency(cat.loadedRate)}/hr
                        </span>
                      </div>
                      <div className="text-xs text-bw-muted mt-0.5">
                        {cat.hours.toLocaleString()} hrs &bull; {cat.headcount} FTE
                        {cat.scaCode && <span> &bull; SCA: {cat.scaCode}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-bw-ink">
                        {formatCurrency(cat.hours * cat.loadedRate)}
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        cat.source === 'deliverables' ? 'bg-success-bg text-success' :
                        cat.source === 'proposal' ? 'bg-bw-primary-tint text-bw-primary' :
                        cat.source === 'staffing' ? 'bg-bw-primary-tint text-bw-primary' :
                        'bg-bw-surface-subtle text-bw-muted'
                      }`}>
                        {cat.source === 'deliverables' ? 'from deliverables' :
                         cat.source === 'proposal' ? 'from proposal' :
                         cat.source === 'staffing' ? 'from staffing plan' :
                         'industry default'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Wrap Rate Breakdown */}
          <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
            <h3 className="font-semibold text-bw-ink mb-1 flex items-center">
              <Calculator className="h-5 w-5 text-success mr-2" />
              Wrap Rate Explained
              <Tip text="The wrap rate is a multiplier that converts a base hourly pay rate into what the government pays. It accounts for all indirect costs of employing someone." />
            </h3>
            <p className="text-xs text-bw-muted mb-3">
              How $100/hr becomes {formatCurrency(100 * pricing.wrapRate)}/hr
            </p>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-bw-primary-tint0 rounded-sm" />
                  <span className="text-sm text-bw-muted">Base Rate</span>
                </div>
                <span className="text-sm font-medium">$100.00</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-success-bg0 rounded-sm" />
                  <div>
                    <span className="text-sm text-bw-muted">+ Fringe ({pricing.indirectRates.fringe}%)</span>
                    <p className="text-[10px] text-bw-muted">Health, PTO, FICA, 401k</p>
                  </div>
                </div>
                <span className="text-sm font-medium">+{formatCurrency(pricing.indirectRates.fringe)}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-fit-moderate-bg0 rounded-sm" />
                  <div>
                    <span className="text-sm text-bw-muted">+ Overhead ({pricing.indirectRates.overhead}%)</span>
                    <p className="text-[10px] text-bw-muted">Rent, IT, management</p>
                  </div>
                </div>
                <span className="text-sm font-medium">
                  +{formatCurrency((1 + pricing.indirectRates.fringe / 100) * pricing.indirectRates.overhead)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-orange-500 rounded-sm" />
                  <div>
                    <span className="text-sm text-bw-muted">+ G&A ({pricing.indirectRates.gAndA}%)</span>
                    <p className="text-[10px] text-bw-muted">Accounting, legal, HR, BD</p>
                  </div>
                </div>
                <span className="text-sm font-medium">
                  +{formatCurrency(
                    (1 + pricing.indirectRates.fringe / 100) *
                    (1 + pricing.indirectRates.overhead / 100) *
                    pricing.indirectRates.gAndA
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-bw-primary-tint0 rounded-sm" />
                  <div>
                    <span className="text-sm text-bw-muted">+ Profit ({pricing.indirectRates.profit}%)</span>
                    <p className="text-[10px] text-bw-muted">Your company's margin</p>
                  </div>
                </div>
                <span className="text-sm font-medium">
                  +{formatCurrency(
                    (1 + pricing.indirectRates.fringe / 100) *
                    (1 + pricing.indirectRates.overhead / 100) *
                    (1 + pricing.indirectRates.gAndA / 100) *
                    pricing.indirectRates.profit
                  )}
                </span>
              </div>
              <div className="border-t border-bw-border pt-2 flex items-center justify-between font-semibold">
                <span className="text-bw-ink">Fully Loaded Rate</span>
                <span className="text-bw-primary">{formatCurrency(100 * pricing.wrapRate)}</span>
              </div>

              {/* Visual bar */}
              <div className="flex h-7 rounded-lg overflow-hidden mt-2">
                <div className="bg-bw-primary-tint0 flex items-center justify-center" style={{ width: `${(100 / (100 * pricing.wrapRate)) * 100}%` }}>
                  <span className="text-[9px] text-white font-medium">Base</span>
                </div>
                <div className="bg-success-bg0 flex items-center justify-center" style={{ width: `${(pricing.indirectRates.fringe / (100 * pricing.wrapRate)) * 100}%` }}>
                  <span className="text-[9px] text-white font-medium">Fr</span>
                </div>
                <div className="bg-fit-moderate-bg0 flex items-center justify-center" style={{ width: `${((1 + pricing.indirectRates.fringe/100) * pricing.indirectRates.overhead / (100 * pricing.wrapRate)) * 100}%` }}>
                  <span className="text-[9px] text-white font-medium">OH</span>
                </div>
                <div className="bg-orange-500 flex items-center justify-center" style={{ width: `${((1 + pricing.indirectRates.fringe/100) * (1 + pricing.indirectRates.overhead/100) * pricing.indirectRates.gAndA / (100 * pricing.wrapRate)) * 100}%` }}>
                  <span className="text-[9px] text-white font-medium">G&A</span>
                </div>
                <div className="bg-bw-primary-tint0 flex items-center justify-center flex-1">
                  <span className="text-[9px] text-white font-medium">Fee</span>
                </div>
              </div>
            </div>
          </div>

          {/* Glossary */}
          <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
            <h3 className="font-semibold text-bw-ink mb-3 flex items-center">
              <HelpCircle className="h-5 w-5 text-bw-muted mr-2" />
              Quick Glossary
            </h3>
            <dl className="space-y-2 text-xs">
              <div>
                <dt className="font-semibold text-bw-ink">CLIN (Contract Line Item Number)</dt>
                <dd className="text-bw-muted">A separate pricing line item on your contract. Each CLIN can have different pricing types and periods.</dd>
              </div>
              <div>
                <dt className="font-semibold text-bw-ink">Base Rate</dt>
                <dd className="text-bw-muted">The direct hourly pay for an employee before any company costs are added.</dd>
              </div>
              <div>
                <dt className="font-semibold text-bw-ink">Loaded Rate</dt>
                <dd className="text-bw-muted">Base rate multiplied by the wrap rate. This is what the government pays per hour.</dd>
              </div>
              <div>
                <dt className="font-semibold text-bw-ink">Wrap Rate</dt>
                <dd className="text-bw-muted">A multiplier that includes fringe benefits, overhead, G&A, and profit on top of the base rate.</dd>
              </div>
              <div>
                <dt className="font-semibold text-bw-ink">ODC (Other Direct Cost)</dt>
                <dd className="text-bw-muted">Non-labor costs charged directly to the contract (travel, equipment, materials).</dd>
              </div>
              <div>
                <dt className="font-semibold text-bw-ink">SCA (Service Contract Act)</dt>
                <dd className="text-bw-muted">Federal law setting minimum wages and benefits for service workers on government contracts.</dd>
              </div>
              <div>
                <dt className="font-semibold text-bw-ink">FFP (Firm Fixed Price)</dt>
                <dd className="text-bw-muted">You set the price upfront and absorb cost overruns. Most common contract type.</dd>
              </div>
              <div>
                <dt className="font-semibold text-bw-ink">T&M (Time and Materials)</dt>
                <dd className="text-bw-muted">Billed hourly with materials at cost. Government bears more risk.</dd>
              </div>
            </dl>
          </div>

          {/* Actions */}
          <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
            <h3 className="font-semibold text-bw-ink mb-3">More Tools</h3>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/workspace/costing/indirect-rates')}
                className="w-full flex items-center gap-2 p-2 text-sm text-left rounded-lg hover:bg-bw-surface-subtle"
              >
                <Calculator className="h-4 w-4 text-success" />
                <div>
                  <span className="block text-bw-ink">Adjust Indirect Rates</span>
                  <span className="block text-xs text-bw-muted">Change fringe, overhead, G&A, profit</span>
                </div>
              </button>
              <button
                onClick={() => navigate('/workspace/costing/labor')}
                className="w-full flex items-center gap-2 p-2 text-sm text-left rounded-lg hover:bg-bw-surface-subtle"
              >
                <Users className="h-4 w-4 text-bw-primary" />
                <div>
                  <span className="block text-bw-ink">Manage Labor Categories</span>
                  <span className="block text-xs text-bw-muted">Set company-wide rates and SCA codes</span>
                </div>
              </button>
              <button
                onClick={() => navigate(`/workspace/${proposalId}/price-to-win`)}
                className="w-full flex items-center gap-2 p-2 text-sm text-left rounded-lg hover:bg-bw-surface-subtle"
              >
                <Target className="h-4 w-4 text-bw-primary" />
                <div>
                  <span className="block text-bw-ink">Price-to-Win Analysis</span>
                  <span className="block text-xs text-bw-muted">Compare against market and competitors</span>
                </div>
              </button>
              <button
                onClick={() => navigate('/workspace/costing/wrap-rate')}
                className="w-full flex items-center gap-2 p-2 text-sm text-left rounded-lg hover:bg-bw-surface-subtle"
              >
                <TrendingUp className="h-4 w-4 text-fit-moderate" />
                <div>
                  <span className="block text-bw-ink">Wrap Rate Calculator</span>
                  <span className="block text-xs text-bw-muted">See how rates compare to industry</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
